import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../utils/errors';
import {
  useInviteCustomerSubuserAdmin,
  useListCustomerSubusers,
  useUpdateCustomerSubuserAdmin,
  type AdminCustomerSubuserRow,
} from '../../features/customers/api';
import CustomerSubuserAddressAccessModal from './CustomerSubuserAddressAccessModal';

const EMPTY = {
  name: '',
  email: '',
};

function statusLabel(status: string) {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'INVITED') return 'Invited';
  if (status === 'DISABLED') return 'Disabled';
  return status || '—';
}

interface CustomerSubusersModalProps {
  customerId: string;
  onClose: () => void;
}

export default function CustomerSubusersModal({ customerId, onClose }: CustomerSubusersModalProps) {
  const { data, isLoading } = useListCustomerSubusers(customerId);
  const inviteSubuser = useInviteCustomerSubuserAdmin(customerId);
  const updateSubuser = useUpdateCustomerSubuserAdmin(customerId);

  const rows = useMemo(() => data?.subusers ?? [], [data]);

  const [mode, setMode] = useState('list'); // list | create
  const [form, setForm] = useState(EMPTY);
  const [managingAddressesFor, setManagingAddressesFor] = useState<AdminCustomerSubuserRow | null>(
    null
  );

  useEffect(() => {
    if (!customerId) return;
    setMode('list');
    setForm(EMPTY);
    setManagingAddressesFor(null);
  }, [customerId]);

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.email.trim()) return 'Email is required';
    return null;
  };

  const onInvite = async () => {
    const err = validate();
    if (err) return toast.warn(err);

    try {
      await inviteSubuser.mutateAsync({
        name: form.name.trim(),
        email: form.email.trim(),
      });
      toast.success('Subuser invite sent.');
      setMode('list');
      setForm(EMPTY);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Invite failed.'));
    }
  };

  const onDisable = async (row: any) => {
    try {
      await updateSubuser.mutateAsync({
        customerUserId: row.id,
        data: { status: 'DISABLED' },
      });
      toast.success('Subuser disabled.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to disable subuser.'));
    }
  };

  const onEnable = async (row: any) => {
    try {
      await updateSubuser.mutateAsync({
        customerUserId: row.id,
        data: { status: 'ACTIVE' },
      });
      toast.success('Subuser enabled.');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to enable subuser.'));
    }
  };

  return (
    <>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal__backdrop" onClick={onClose} />
        <div className="modal__panel" style={{ maxWidth: 1000, width: '100%' }}>
          <div className="modal__header">
            <h3 className="modal__title">Customer Subusers</h3>
            <button className="btn btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>

          <div className="modal__body">
            <div className="flex-between" style={{ marginBottom: 12 }}>
              <div className="muted">
                {rows.length} subuser{rows.length === 1 ? '' : 's'}
              </div>

              {mode === 'list' && (
                <button className="btn btn--primary" onClick={() => setMode('create')}>
                  Invite Subuser
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="skeleton" style={{ height: 180 }} />
            ) : mode === 'list' ? (
              rows.length === 0 ? (
                <div className="muted">No subusers yet.</div>
              ) : (
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Address Access</th>
                        <th>Updated</th>
                        <th className="ta-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.name || r.user?.name || <span className="muted">—</span>}</td>
                          <td>{r.email}</td>
                          <td>{statusLabel(r.status)}</td>
                          <td>{r.enabledAddressCount ?? 0}</td>
                          <td>{new Date(r.updatedAt).toLocaleString()}</td>
                          <td className="ta-right">
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <button
                                className="btn btn--ghost"
                                onClick={() => setManagingAddressesFor(r)}
                              >
                                Manage Addresses
                              </button>

                              {r.status === 'ACTIVE' ? (
                                <button
                                  className="btn btn--ghost"
                                  onClick={() => onDisable(r)}
                                  disabled={updateSubuser.isPending}
                                >
                                  Disable
                                </button>
                              ) : r.status === 'DISABLED' ? (
                                <button
                                  className="btn btn--ghost"
                                  onClick={() => onEnable(r)}
                                  disabled={updateSubuser.isPending}
                                >
                                  Enable
                                </button>
                              ) : (
                                <button className="btn btn--ghost" disabled>
                                  Invited
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <>
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-control">
                    <label>Name *</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Jane Smith"
                    />
                  </div>

                  <div className="form-control">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="name@company.com"
                    />
                  </div>
                </div>

                <div
                  className="actions"
                  style={{ marginTop: 14, gap: 8, justifyContent: 'flex-end' }}
                >
                  <button
                    className="btn btn--primary"
                    onClick={onInvite}
                    disabled={inviteSubuser.isPending}
                  >
                    {inviteSubuser.isPending ? 'Sending…' : 'Send Invite'}
                  </button>
                  <button
                    className="btn btn--ghost"
                    onClick={() => {
                      setMode('list');
                      setForm(EMPTY);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {managingAddressesFor && (
        <CustomerSubuserAddressAccessModal
          customerId={customerId}
          subuser={managingAddressesFor}
          onClose={() => setManagingAddressesFor(null)}
        />
      )}
    </>
  );
}
